import { Link } from "react-router-dom";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { listDeploymentGroups, type DeploymentGroup } from "@/lib/api";
import { groupMeta, groupStatus, serviceStatus, timeAgo } from "@/lib/deployments";
import { Avatar } from "@/components/Avatar";
import { EntityLogo } from "@/components/EntityLogo";

/** Shared with a deployment's own page, so opening one from here is instant. */
export const deploymentGroupsQuery = queryOptions({
  queryKey: ["deployments", "groups"],
  queryFn: listDeploymentGroups,
  // Poll faster while anything is mid-deploy, like the Render dashboard.
  refetchInterval: (q) =>
    q.state.data?.groups.some((g) => g.services.some((s) => s.latestDeploy?.inProgress))
      ? 5_000
      : 30_000,
});

export function DeploymentsPage() {
  const { data, isLoading, isError } = useQuery(deploymentGroupsQuery);

  return (
    <div>
      <header className="mb-8">
        <h1 className="page-title">Deployments</h1>
        <p className="mt-2 text-text-secondary">
          Live status of our Render services.
        </p>
      </header>

      {isLoading && (
        <div className="py-16 text-center text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-xl" />
        </div>
      )}

      {(isError || (data && !data.configured)) && <NotConfigured />}

      {data?.configured && (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.groups.map((g) => (
            <GroupCard key={g.key} group={g} />
          ))}
          {data.groups.length === 0 && (
            <p className="text-sm text-text-secondary">No Render services found for this API key.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** One deployment: its overall status, then a row per service in it. */
function GroupCard({ group }: { group: DeploymentGroup }) {
  const st = groupStatus(group.services);
  // The first service that has one stands for the deployment.
  const url = group.services.find((s) => s.url)?.url ?? null;
  const logoUrl = group.services.find((s) => s.logoUrl)?.logoUrl ?? null;
  const owner = group.services.find((s) => s.owner)?.owner ?? null;

  return (
    <Link
      to={`/deployments/${group.key}`}
      className="group flex flex-col rounded-token-lg border border-border bg-bg-card p-5 shadow-token-sm transition-all hover:border-accent hover:shadow-token-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <EntityLogo name={group.name} url={logoUrl} size={34} fallbackIcon="fa-server" />
          <div className="min-w-0">
            <h3 className="truncate font-display text-lg font-semibold text-text-heading">
              {group.name}
            </h3>
            <p className="mt-0.5 text-xs text-text-muted">
              {[
                groupMeta(group.services),
                group.services.length > 1 && `${group.services.length} microservices`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </div>
        {st && <StatusLabel {...st} />}
      </div>

      <ul className="mt-4 divide-y divide-border-light border-t border-border-light text-sm">
        {group.services.map((s) => {
          const sst = serviceStatus(s);
          return (
            <li key={s.id} className="py-2.5">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${sst?.inProgress ? "animate-pulse" : ""}`}
                  style={{ background: sst?.color ?? "transparent" }}
                  title={sst?.label}
                />
                <span className="truncate font-medium text-text-heading">{s.name}</span>
                {s.role && (
                  <span className="shrink-0 rounded-full bg-bg-secondary px-2 py-0.5 text-[0.65rem] text-text-muted">
                    {s.role}
                  </span>
                )}
                {s.latestDeploy && (
                  <span className="ml-auto shrink-0 text-xs text-text-muted">
                    {timeAgo(s.latestDeploy.finishedAt ?? s.latestDeploy.createdAt)}
                  </span>
                )}
              </div>
              <p className="mt-0.5 truncate pl-4 text-xs text-text-secondary">
                {s.latestDeploy ? (
                  <>
                    {s.latestDeploy.commitId && (
                      <span className="mr-2 font-mono text-text-muted">{s.latestDeploy.commitId}</span>
                    )}
                    {s.latestDeploy.commitMessage ?? "Deploy"}
                  </>
                ) : (
                  <span className="text-text-muted">No deploys yet</span>
                )}
              </p>
            </li>
          );
        })}
      </ul>

      {(url || owner) && (
        <div className="mt-auto flex items-center gap-3 border-t border-border-light pt-3 text-xs text-text-muted">
          {url && (
            <span
              role="link"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                window.open(url, "_blank", "noopener");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") window.open(url, "_blank", "noopener");
              }}
              className="inline-flex items-center gap-1 truncate hover:text-accent"
              title={url}
            >
              <i className="fa-solid fa-globe" />
              {url.replace(/^https?:\/\//, "")}
            </span>
          )}
          {owner && (
            <span
              className="ml-auto inline-flex shrink-0 items-center gap-1.5"
              title={`Owner: ${owner.name}`}
            >
              <Avatar name={owner.name} url={owner.avatarUrl} size={18} />
              {owner.name.split(" ")[0]}
            </span>
          )}
        </div>
      )}
    </Link>
  );
}

export function StatusLabel({
  label,
  color,
  inProgress,
}: {
  label: string;
  color: string;
  inProgress: boolean;
}) {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
      <span
        className={`h-2 w-2 rounded-full ${inProgress ? "animate-pulse" : ""}`}
        style={{ background: color }}
      />
      <span style={{ color }}>{label}</span>
    </span>
  );
}

function NotConfigured() {
  return (
    <div className="rounded-token-lg border border-dashed border-border bg-bg-card/40 px-6 py-16 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-token-lg bg-accent-muted text-xl text-accent">
        <i className="fa-solid fa-plug" />
      </div>
      <h2 className="mt-4 font-display text-lg font-semibold text-text-heading">
        Connect Render
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
        Add a <code className="font-mono text-xs">RENDER_API_KEY</code> environment variable
        to the portal service in Render (Account Settings → API Keys), then redeploy. Service
        status and deploy history will appear here.
      </p>
    </div>
  );
}
